import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Send } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { Button } from '../../components/Shared/Button';
import { Card } from '../../components/Shared/Card';
import { FormField } from '../../components/Shared/FormField';
import { Input } from '../../components/Shared/Input';
import { useSupportStore } from '../../store/useSupportStore';
import {
  BusinessPlan,
  SupportTicketType,
  type CreateSupportTicketRequest,
} from '../../api/types/admin';
import { PublicShell } from './PublicShell';

const PLAN_NAME: Record<number, string> = {
  [BusinessPlan.Free]: 'Free',
  [BusinessPlan.Starter]: 'Starter',
  [BusinessPlan.Professional]: 'Professional',
  [BusinessPlan.Enterprise]: 'Enterprise',
};

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation(['public', 'common']);
  const { create, saving } = useSupportStore();

  const planParam = searchParams.get('plan');
  const planNumber = planParam != null ? Number(planParam) : null;
  const planName = planNumber != null && PLAN_NAME[planNumber] ? PLAN_NAME[planNumber] : null;

  const [type, setType] = useState<SupportTicketType>(
    planName ? SupportTicketType.NewTenantRequest : SupportTicketType.Question,
  );
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState(
    planName ? t('public:contact.subjectPrefill', { plan: planName }) : '',
  );
  const [message, setMessage] = useState('');
  const [submittedId, setSubmittedId] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  useEffect(() => {
    if (planName) {
      setType(SupportTicketType.NewTenantRequest);
      setSubject(t('public:contact.subjectPrefill', { plan: planName }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planParam]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldError(null);

    if (!email.includes('@')) {
      setFieldError(t('public:contact.errors.emailInvalid'));
      return;
    }
    if (!subject.trim()) {
      setFieldError(t('public:contact.errors.subjectRequired'));
      return;
    }
    if (!message.trim()) {
      setFieldError(t('public:contact.errors.messageRequired'));
      return;
    }

    const req: CreateSupportTicketRequest = {
      email,
      name: name || undefined,
      subject: subject.trim(),
      message: message.trim(),
      type,
    };
    const id = await create(req, true);
    if (id) {
      setSubmittedId(id);
    }
  };

  if (submittedId) {
    return (
      <PublicShell>
        <Card className="max-w-xl mx-auto text-center py-10">
          <h2 className="text-[24px] font-semibold text-slate-900 tracking-tight">
            {t('public:contact.success.title')}
          </h2>
          <p className="text-[13px] text-slate-600 mt-2">
            {t('public:contact.success.message', { id: submittedId.slice(0, 8) })}
          </p>
          <div className="mt-5">
            <Link to="/pricing">
              <Button
                variant="secondary"
                leftIcon={<ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />}
              >
                {t('public:contact.success.backToPricing')}
              </Button>
            </Link>
          </div>
        </Card>
      </PublicShell>
    );
  }

  return (
    <PublicShell>
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-[28px] font-semibold text-slate-900 tracking-tight">
            {t('public:contact.title')}
          </h1>
          <p className="text-[13px] text-slate-500 mt-1">{t('public:contact.subtitle')}</p>
        </div>

        <Card>
          <form onSubmit={handleSubmit} className="space-y-4">
            <FormField label={t('public:contact.fields.type')} required>
              <select
                value={type}
                onChange={(e) => setType(Number(e.target.value) as SupportTicketType)}
                className="w-full px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30"
              >
                <option value={SupportTicketType.Question}>{t('public:contact.types.question')}</option>
                <option value={SupportTicketType.Problem}>{t('public:contact.types.problem')}</option>
                <option value={SupportTicketType.NewTenantRequest}>
                  {t('public:contact.types.newTenantRequest')}
                </option>
                <option value={SupportTicketType.MeetingRequest}>
                  {t('public:contact.types.meetingRequest')}
                </option>
                <option value={SupportTicketType.Other}>{t('public:contact.types.other')}</option>
              </select>
            </FormField>

            <FormField label={t('public:contact.fields.name')}>
              <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
            </FormField>

            <FormField label={t('public:contact.fields.email')} required>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                maxLength={256}
              />
            </FormField>

            <FormField label={t('public:contact.fields.subject')} required>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
                maxLength={200}
              />
            </FormField>

            <FormField label={t('public:contact.fields.message')} required>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                required
                maxLength={5000}
                className="w-full px-3 py-2 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 resize-y"
              />
            </FormField>

            {fieldError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">
                {fieldError}
              </div>
            )}

            <Button
              type="submit"
              disabled={saving}
              leftIcon={<Send className="w-3.5 h-3.5" strokeWidth={2} />}
              className="w-full justify-center"
            >
              {saving ? t('public:contact.submitting') : t('public:contact.submit')}
            </Button>
          </form>
        </Card>
      </div>
    </PublicShell>
  );
}
